import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../utils/apiClient";
import { getUserFromToken } from "../utils/jwtUtils";

export interface BusinessProfile {
  business_name: string | null;
  business_domain: string | null;
  business_category: string | null;
  city: string | null;
  country: string | null;
  neighbourhood?: string | null;
  price_tier?: string | null;
  amenities?: string[];
  email?: string;
  phone_number?: string | null;
  is_complete: boolean;
}

type AuthUser = ReturnType<typeof getUserFromToken>;

interface AuthContextValue {
  user: AuthUser;
  businessProfile: BusinessProfile | null;
  refreshUser: () => void;
  refreshSession: () => Promise<AuthUser>;
  refreshBusinessProfile: () => Promise<void>;
  updateBusinessProfile: (profile: BusinessProfile) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Must be rendered inside <Router> — logout calls useNavigate.
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState<AuthUser>(() =>
    getUserFromToken(localStorage.getItem("accessToken")),
  );
  const [profileRecord, setProfileRecord] = useState<{
    ownerId: string;
    profile: BusinessProfile;
  } | null>(null);
  const businessProfile = user && profileRecord?.ownerId === user.id ? profileRecord.profile : null;

  const refreshUser = useCallback(() => {
    setUser(getUserFromToken(localStorage.getItem("accessToken")));
  }, []);

  // Re-mint the JWT from the server so plan/limit claims pick up out-of-band changes (e.g. a
  // plan switch made in the Stripe Billing Portal). The token is the source of truth for the
  // UI, so we swap it in localStorage and update state. Non-blocking: returns the current user
  // on any failure.
  const refreshSession = useCallback(async (): Promise<AuthUser> => {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    try {
      const res = await api.post("/refresh-token");
      const newToken = res.data?.access_token;
      if (newToken) {
        localStorage.setItem("accessToken", newToken);
        const nextUser = getUserFromToken(newToken);
        setUser(nextUser);
        return nextUser;
      }
    } catch {
      // non-blocking
    }
    return getUserFromToken(localStorage.getItem("accessToken"));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("accessToken");
    setUser(null);
    setProfileRecord(null);
    navigate("/");
  }, [navigate]);

  // Reads fresh from localStorage so this callback never goes stale.
  const refreshBusinessProfile = useCallback(async () => {
    const currentUser = getUserFromToken(localStorage.getItem("accessToken"));
    if (!currentUser) {
      setProfileRecord(null);
      return;
    }
    try {
      const res = await api.get("/user/profile");
      setProfileRecord({ ownerId: currentUser.id, profile: res.data });
    } catch {
      // non-blocking
    }
  }, []);

  const updateBusinessProfile = useCallback((profile: BusinessProfile) => {
    const currentUser = getUserFromToken(localStorage.getItem("accessToken"));
    if (currentUser) setProfileRecord({ ownerId: currentUser.id, profile });
  }, []);

  // Fetch profile on login / logout (keyed by user id, not object reference).
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api
      .get("/user/profile")
      .then((res) => {
        if (!cancelled) setProfileRecord({ ownerId: userId, profile: res.data });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      setProfileRecord(null);
    };
  }, [userId]);

  // On return from the Stripe Billing Portal (return_url carries ?billing=updated), refresh the
  // session so a plan switch shows up without a re-login. The webhook that updates our records
  // can lag the browser redirect, so poll a few times until the plan reflects the change, then
  // strip the marker so a reload doesn't re-trigger. Runs once on mount (Stripe does a full-page
  // redirect back into the app).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") !== "updated") return undefined;

    let cancelled = false;
    const previousPlan =
      getUserFromToken(localStorage.getItem("accessToken"))?.subscriptionPlan ?? null;

    const pollForUpdatedPlan = async () => {
      const maxTries = 6;
      for (let i = 0; i < maxTries && !cancelled; i++) {
        const nextUser = await refreshSession();
        if (nextUser && nextUser.subscriptionPlan !== previousPlan) break;
        if (i < maxTries - 1) await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      if (cancelled) return;
      const url = new URL(window.location.href);
      url.searchParams.delete("billing");
      window.history.replaceState({}, "", url.toString());
    };

    pollForUpdatedPlan();
    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  // Pick up authChange events dispatched by components outside this context
  // (AuditEngine, VisibilityEngine, Checkout, etc.) and cross-tab changes.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accessToken") refreshUser();
    };
    window.addEventListener("authChange", refreshUser);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("authChange", refreshUser);
      window.removeEventListener("storage", onStorage);
    };
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        businessProfile,
        refreshUser,
        refreshSession,
        refreshBusinessProfile,
        updateBusinessProfile,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
